import { useState } from 'react';
import { Alert, Group, Stack, Text, Title } from '@mantine/core';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient';
import { clientIdentity } from '@/context/ApiClientContext/identity';
import StyledButton from '@/components/StyledButton/StyledButton';
import classes from './ForgeAccess.module.css';

type Connection = {
  id: string;
  device_label: string;
  environment: string;
  org_id: string;
  capabilities: string[];
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
};
type ConnectionPage = { results: Connection[]; next_offset?: number | null };
const options = { suppressGlobalErrorToast: true, handleForbiddenLocally: true };

export default function CliConnections() {
  const api = useApiClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  const query = useInfiniteQuery({
    queryKey: ['cli-connections', clientIdentity(api)],
    meta: { suppressGlobalErrorToast: true },
    initialPageParam: 0,
    getNextPageParam: (page: ConnectionPage) => page.next_offset ?? undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<ConnectionPage>('/internal/forge-auth/sessions', {
        ...options,
        params: { offset: pageParam },
      });
      if (
        !data ||
        !Array.isArray(data.results) ||
        data.results.some(
          (row) =>
            !row ||
            !row.id ||
            !Array.isArray(row.capabilities) ||
            typeof row.expires_at !== 'string' ||
            !Number.isFinite(Date.parse(row.expires_at)),
        ) ||
        (data.next_offset != null &&
          (!Number.isSafeInteger(data.next_offset) || data.next_offset <= pageParam))
      ) {
        throw new Error('Invalid connection response.');
      }
      return data;
    },
    retry: false,
  });
  async function revoke(id: string) {
    setBusy(id);
    setError('');
    try {
      await api.delete(`/internal/forge-auth/sessions/${encodeURIComponent(id)}`, options);
      await query.refetch({ throwOnError: true });
    } catch {
      setError('Revocation was not confirmed. Reload connections to check the current state.');
    } finally {
      setBusy(null);
    }
  }
  async function reload() {
    try {
      await query.refetch({ throwOnError: true });
      setError('');
    } catch {
      // Keep an uncertain revocation blocked until its state can be read.
    }
  }
  return (
    <Stack gap="sm">
      <Title order={2}>Your CLI connections</Title>
      <Text size="sm">
        Revoking a connection blocks new CLI requests. It does not cancel accepted work or change
        automation grants.
      </Text>
      {query.isPending && <Text>Loading connections…</Text>}
      {query.error && <Alert color="red">Connections could not be loaded.</Alert>}
      {error && <Alert color="red">{error}</Alert>}
      {query.data?.pages[0].results.length === 0 && <Text>No CLI connections.</Text>}
      {query.data?.pages
        .flatMap((page) => page.results)
        .map((connection) => {
          const expired = Date.parse(connection.expires_at) <= Date.now();
          const inactive = Boolean(connection.revoked_at) || expired;
          return (
            <Group key={connection.id} justify="space-between" align="flex-start" wrap="wrap">
              <Stack gap={2} style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <Text fw={600}>{connection.device_label || 'CLI connection'}</Text>
                <Text size="sm">
                  {connection.environment} · {connection.capabilities.join(', ')}
                </Text>
                <Text size="sm">
                  {connection.org_id
                    ? `Restricted to ${connection.org_id}`
                    : 'Authorized organizations'}
                </Text>
                <Text size="sm">
                  {connection.revoked_at ? 'Revoked' : expired ? 'Expired' : 'Active'} · Expires{' '}
                  {new Date(connection.expires_at).toLocaleString()}
                </Text>
              </Stack>
              <StyledButton
                variant="outline"
                className={classes.secondary}
                aria-label={`Revoke ${connection.device_label || 'CLI connection'}`}
                disabled={
                  inactive || busy !== null || Boolean(error) || query.isFetching || query.isError
                }
                loading={busy === connection.id}
                onClick={() => void revoke(connection.id)}
              >
                Revoke
              </StyledButton>
            </Group>
          );
        })}
      {query.hasNextPage && (
        <StyledButton
          variant="subtle"
          className={classes.secondary}
          disabled={busy !== null || query.isFetching}
          loading={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          Load more connections
        </StyledButton>
      )}
      <StyledButton
        variant="subtle"
        className={classes.secondary}
        disabled={busy !== null || query.isFetching}
        onClick={() => void reload()}
      >
        Reload connections
      </StyledButton>
    </Stack>
  );
}
